import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AvatarNode,
  SvgIcon,
  UserCell,
  UserCellProps,
} from '../../../Molecules';

import { SVGIconType } from '../../../../constants/svgIcon';
import { Profile } from '../../../../models/UserStore';

import styles from './LearningPackageReferralTemplate.module.css';

export type LearningPackageReferralTemplateProps = {
  totalReferralUserCount: number;
  profile: Profile | undefined | null;
  confirmReward: string;
  potentialReward: string;
  cashOutRequirement: string;
  data: UserCellProps[];
  onClickInfo: () => void;
  onClickCashOut: () => void;
  onClickShare: () => void;
};

export const LearningPackageReferralTemplate = memo(
  ({
    totalReferralUserCount,
    profile,
    confirmReward,
    potentialReward,
    cashOutRequirement,
    data,
    onClickInfo,
    onClickCashOut,
    onClickShare,
  }: LearningPackageReferralTemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className={styles.learningReferral}>
        <div className={styles.centeredContainer}>
          {!profile ? (
            <div className={styles.container}>
              <SvgIcon
                name={SVGIconType.Referral}
                className={styles.referralIcon}
                stroke={'#000'}
              />
              <div className={styles.referralText}>
                <span>{totalReferralUserCount}</span>
                {t('Referrals')}
              </div>
              <SvgIcon
                name={SVGIconType.GameInstruction}
                className={styles.referralInfo}
                stroke={'#000'}
                onClick={onClickInfo}
              />
            </div>
          ) : null}
          {profile ? (
            <div className={styles.container}>
              <AvatarNode
                avatar={{
                  ...profile?.avatar,
                  userId: profile?.userId,
                  source:
                    profile?.profilePicture &&
                    profile?.profilePicture.publicUrls?.small
                      ? { uri: profile?.profilePicture.publicUrls?.small }
                      : '',
                }}
                size={80}
              />
              <div className={styles.column}>
                <div className={styles.userName}>{profile?.name}</div>
                <div className={styles.userDescription}>
                  <span>
                    {profile.statistics.smartParentsPlanReferralCount ?? 0}
                  </span>
                  {t('Referred Users')}
                </div>
              </div>
            </div>
          ) : null}
          {!profile ? (
            <div className={styles.whiteBanner}>
              <div className={styles.columnBannerText}>
                <div className={styles.centerText}>
                  <div className={styles.rewardTotalText}>
                    {t('Confirmed Rewards (Total): ')}
                    <span className={styles.confirmedSpan}>
                      {confirmReward}
                    </span>
                  </div>
                  <div className={styles.rewardTotalText}>
                    {t('Potential Rewards (Total): ')}
                    <span className={styles.potentialSpan}>
                      {potentialReward}
                    </span>
                  </div>
                </div>

                <button
                  className={styles.cashOutButton}
                  onClick={onClickCashOut}
                >
                  {t('Cash Out')}
                </button>
                <div className={styles.rewardDescription}>
                  <p className={styles.rewardDescriptionText}>
                    {t(
                      '* JOY Cash can be redeemed once you accumulate {amount} or more. Redemption requests must be submitted one month in advance and will be processed on the 15th of each month.',
                      { amount: cashOutRequirement },
                    )}
                  </p>
                  <p className={styles.rewardDescriptionText}>
                    {t(
                      'Your reward will be distributed over two months. You will receive 50% of your reward in the first month and the remaining 50% in the following month.',
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          <div className={styles.paleBanner}>
            <div className={styles.columnBannerText}>
              <div className={styles.centerText}>
                {t(
                  'New users that you and your referred users invited to JOYAJOY’s Smart Parents Program. When users purchased the program, you will receive rewards!',
                )}
              </div>

              <button
                className={styles.shareReferButton}
                onClick={onClickShare}
              >
                {t('Share & Refer')}
              </button>
            </div>
          </div>
          <div className={styles.listTitle}>
            {profile
              ? t('Referred Users (Tier 2)')
              : t('Referred Users (Tier 1)')}
          </div>
          <div className={styles.listView}>
            {data.length === 0 ? (
              <div className={styles.emptyContainer}>
                <SvgIcon
                  name={SVGIconType.DoublePerson}
                  className={styles.emptyReferralIcon}
                  stroke={'#000'}
                />
                <div className={styles.emptyReferralText}>
                  {t('No Users Referred')}
                </div>
              </div>
            ) : (
              data.map((item, index) => (
                <Fragment key={index}>
                  {index != 0 && <div className={styles.cellSpace} />}
                  <UserCell key={index} {...item} />
                </Fragment>
              ))
            )}
          </div>
        </div>
      </div>
    );
  },
);
